/**
 * Parsed time object
 */
interface ParsedTime {
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Parse time string to { hours, minutes, seconds }
 */
function parseTime(value: string | null | undefined): ParsedTime | null {
  if (!value) return null;

  // Handle HH:MM:SS or H:MM:SS format
  const matchWithSeconds = value.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (matchWithSeconds) {
    const hours = parseInt(matchWithSeconds[1], 10);
    const minutes = parseInt(matchWithSeconds[2], 10);
    const seconds = parseInt(matchWithSeconds[3], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59) {
      return { hours, minutes, seconds };
    }
  }

  // Handle HH:MM or H:MM format
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return { hours, minutes, seconds: 0 };
    }
  }

  return null;
}

/**
 * Calculate hours between two times
 * @returns Decimal hours (e.g., 8.5 for 8 hours 30 minutes)
 */
export function calculateHours(startTime: string | null, endTime: string | null): number | null {
  const start = parseTime(startTime);
  const end = parseTime(endTime);

  if (!start || !end) return null;

  const startSeconds = start.hours * 3600 + start.minutes * 60 + start.seconds;
  const endSeconds = end.hours * 3600 + end.minutes * 60 + end.seconds;

  // Handle crossing midnight (e.g., 22:00 to 02:00)
  let diff = endSeconds - startSeconds;
  if (diff < 0) {
    diff += 24 * 3600; // Add 24 hours
  }

  return Math.round((diff / 3600) * 100) / 100; // Round to 2 decimal places
}

/**
 * Format time to HH:mm string
 */
export function formatTime(hours: number, minutes: number): string {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Format time with seconds to HH:mm:ss string
 */
export function formatTimeWithSeconds(hours: number, minutes: number, seconds: number): string {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * A wall-clock time as `'HH:mm'` — the wire format of every time the
 * timesheet API accepts (`localTimeSchema`, docs/DATES.md "Venue wall-clock
 * values"). Postgres returns `time` columns as `'HH:mm:ss'`; this drops the
 * seconds. `null` / `''` → `null`; a value that is not a time passes through
 * unchanged, so the API's validation (not this helper) refuses it.
 */
export function formatTimeHHmm(time: string | null | undefined): string | null {
  if (!time) return null;
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return time;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}
